import { AssetContainerTypes } from 'js/src/client';
import type {
    MediaResultsTable,
    StoredAssetContainer,
    StoredMediaResult,
} from 'js/src/data/media/media-types';
import {
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media/media-types';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import type { StoredUploadedAsset } from 'js/src/data/uploads/uploads-types';
import {
    readonlyAssetContainerModelIsAudio,
    readonlyAssetContainerModelIsBackground,
    readonlyAssetContainerModelIsIcon,
} from 'js/src/data/uploads/uploads-types';

// Where removeUpload takes an upload out of each table, and what it takes, so restoreUpload can put it back.

export type RemovedRecord<T> = { readonly record: T; readonly index: number };

type RemovableAsset = StoredAssetContainer | StoredMediaResult;

const findSoleRecord = <T extends object, ID extends string | number>(
    table: PlainJsStoreOrmData<T, ID>,
    matches: (record: T) => boolean,
): RemovedRecord<T> | null => {
    const matchingIndexes = table.allIds.flatMap((id, index) =>
        matches(table.byId[id]) ? [index] : [],
    );
    if (matchingIndexes.length !== 1) {
        return null;
    }
    const index = matchingIndexes[0];
    return { record: table.byId[table.allIds[index]], index };
};

const searchableUuid = (asset: RemovableAsset): string | null => {
    if (readonlyAssetContainerModelIsBackground(asset)) {
        const background = asset.background;
        if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
            return background.image.uuid;
        }
        if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
            return background.video.uuid;
        }
        throw new Error('unknown slide background type');
    }
    if (readonlyAssetContainerModelIsIcon(asset)) {
        return asset.icon.uuid;
    }
    return null;
};

export const findRemovedMediaResult = (
    table: MediaResultsTable,
    asset: RemovableAsset,
): RemovedRecord<StoredMediaResult> | null => {
    const uuid = searchableUuid(asset);
    if (!uuid) {
        return null;
    }
    return findSoleRecord(table, (record) => searchableUuid(record) === uuid);
};

export const findRemovedAudioResult = (
    table: MediaResultsTable,
    audioId: number,
): RemovedRecord<StoredMediaResult> | null =>
    findSoleRecord(
        table,
        (record) => readonlyAssetContainerModelIsAudio(record) && record.audio.id === audioId,
    );

export function uploadedAssetMatchesRemovedAsset(
    removedAsset: RemovableAsset,
): (uploadedAsset: StoredUploadedAsset) => boolean {
    if (readonlyAssetContainerModelIsBackground(removedAsset)) {
        const background = removedAsset.background;
        if (readonlySlideBackgroundModelIsVideoBackgroundModel(background)) {
            const uuid = background.video.uuid;
            return (uploadedAsset) =>
                uploadedAsset.type === AssetContainerTypes.BACKGROUND &&
                uploadedAsset.asset.video?.uuid === uuid;
        }
        if (readonlySlideBackgroundModelIsImageBackgroundModel(background)) {
            const uuid = background.image.uuid;
            return (uploadedAsset) =>
                uploadedAsset.type === AssetContainerTypes.BACKGROUND &&
                uploadedAsset.asset.image?.uuid === uuid;
        }
        throw new Error('removeUpload: attempting to remove a background that is not supported');
    }
    if (readonlyAssetContainerModelIsAudio(removedAsset)) {
        const uuid = removedAsset.audio.uuid;
        return (uploadedAsset) =>
            uploadedAsset.type !== AssetContainerTypes.BACKGROUND &&
            uploadedAsset.asset.uuid === uuid;
    }
    if (readonlyAssetContainerModelIsIcon(removedAsset)) {
        const uuid = removedAsset.icon.uuid;
        return (uploadedAsset) =>
            uploadedAsset.type !== AssetContainerTypes.BACKGROUND &&
            uploadedAsset.asset.uuid === uuid;
    }
    throw Error('removeUpload: attempting to remove an asset that is not supported');
}

export const findRemovedUploadedAsset = (
    table: PlainJsStoreOrmData<StoredUploadedAsset, string>,
    asset: RemovableAsset,
): RemovedRecord<StoredUploadedAsset> | null =>
    findSoleRecord(table, uploadedAssetMatchesRemovedAsset(asset));
